import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { apiAttendance } from "../api/attendance";

export const ET = "America/New_York";
export const clockTime = (value) => value ? DateTime.fromISO(value).setZone(ET).toFormat("h:mm a") + " ET" : "—";
const clockDateTime = (value) => value ? DateTime.fromISO(value).setZone(ET).toFormat("LLL d, yyyy h:mm a") + " ET" : "—";
export const duration = (seconds) => `${Math.floor(Math.max(0, seconds) / 3600)}h ${Math.floor(Math.max(0, seconds) % 3600 / 60)}m`;
export const clockLabel = (status) => ({ clocked_in: "Clocked in", on_break: "On break", clocked_out: "Clocked out" }[status] || "Clock unavailable");
const reasons = { manual: "Clocked out", inactivity: "Automatic · inactivity", connection_lost: "Automatic · connection lost", added: "Added by employee" };

export function AttendanceDetails({ state, onCorrect, onAdd }) {
  if (!state) {return null;}
  return <div className="tw-text-sm tw-text-gray-700">
    <dl className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-1 tw-mb-3">
      <dt>Clocked time</dt><dd className="tw-text-right tw-font-semibold tw-m-0">{duration(state.clockedSeconds)}</dd>
      <dt>Breaks</dt><dd className="tw-text-right tw-m-0">{duration(state.breakSeconds)}</dd>
      <dt>Excluding breaks</dt><dd className="tw-text-right tw-font-semibold tw-m-0">{duration(state.netSeconds)}</dd>
      <dt>Original excluding breaks</dt><dd className="tw-text-right tw-m-0">{duration(state.recordedNetSeconds)}</dd>
    </dl>
    {!state.sessions.length && <p className="tw-text-gray-500">No clock records for this date.</p>}
    {state.sessions.map((s) => <div key={s.id} className="tw-border-t tw-border-gray-200 tw-py-2">
      <div className="tw-flex tw-flex-wrap tw-justify-between tw-gap-1">
        <strong>{clockDateTime(s.startedAt)} – {s.endedAt ? clockDateTime(s.endedAt) : "Open"}</strong>
        {onCorrect && s.endedAt && <button type="button" className="tw-text-blue-800 tw-underline" onClick={() => onCorrect(s)}>Correct time</button>}
      </div>
      <div className="tw-text-xs tw-text-gray-500">{reasons[s.endReason] || "Session running"}{s.corrected ? " · Employee corrected" : ""}</div>
      {s.corrected && !s.addedManually && <div className="tw-text-xs tw-text-gray-600 tw-mt-1">
        Original: {clockDateTime(s.recordedStartedAt)} – {clockDateTime(s.recordedEndedAt)}
      </div>}
      {(s.breaks || []).map((b) => <div key={b.id} className="tw-text-xs tw-mt-1">Break: {clockTime(b.startedAt)} – {b.endedAt ? clockTime(b.endedAt) : "In progress"}</div>)}
      {s.callVerification === "not_linked" && <p className="tw-text-xs tw-text-yellow-800 tw-mb-0">No linked phone account; calls cannot keep the clock running.</p>}
      {s.callVerification === "not_configured" && <p className="tw-text-xs tw-text-yellow-800 tw-mb-0">Call verification is not configured.</p>}
      {s.callVerification === "unavailable" && <p className="tw-text-xs tw-text-yellow-800 tw-mb-0">Call verification unavailable; automatic time may need correcting.</p>}
      {(s.changes || []).filter((c) => c.action === "correct" || c.action === "add").map((c) => <details key={c.id} className="tw-text-xs tw-mt-2">
        <summary className="tw-cursor-pointer">{c.action === "add" ? "Added" : "Corrected"} {DateTime.fromISO(c.createdAt).setZone(ET).toFormat("LLL d, h:mm a")} ET · {c.reason}</summary>
        <ChangeTimes change={c} />
      </details>)}
    </div>)}
    {onAdd && <button type="button" onClick={onAdd} className="tw-mt-2 tw-text-blue-800 tw-underline">Add missed session</button>}
    <p className="tw-text-xs tw-text-gray-500 tw-mt-3 tw-mb-0">Attendance only, not payroll. Recorded activity is measured separately. Corrections retain the original record.</p>
  </div>;
}

function ChangeTimes({ change }) {
  const read = (raw) => { try { return JSON.parse(raw); } catch (_) { return null; } };
  const before = read(change.before);
  const after = read(change.after);
  const show = (record) => record ? <>
    <div>{clockDateTime(record.session.startedAt)} – {clockDateTime(record.session.endedAt)}</div>
    {(record.breaks || []).map((b, i) => <div key={i}>Break: {clockTime(b.startedAt)} – {clockTime(b.endedAt)}</div>)}
  </> : <div>No original session</div>;
  return <div className="tw-bg-gray-50 tw-p-2 tw-mt-1"><strong>Before</strong>{show(before)}<strong>After</strong>{show(after)}</div>;
}

export function ManagerAttendance({ userId }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    apiAttendance.getUser(userId).then((result) => { if (!cancelled) {setState(result);} })
      .catch(() => { if (!cancelled) {setError(true);} });
    return () => { cancelled = true; };
  }, [userId]);
  return <div className="tw-border-t tw-border-gray-200 tw-p-4">
    <div className="tw-flex tw-justify-between tw-mb-3"><strong className="tw-text-sm">Attendance · Today ET</strong>
      <span className="tw-text-xs">{state ? state.latest ? clockLabel(state.status) : "No clock records" : error ? "Unavailable" : "Loading…"}</span></div>
    {state && <AttendanceDetails state={state} />}
  </div>;
}
